import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import postHog, { type CaptureResult } from 'posthog-js';
import useVersionsStore from '@/common/store/pinia/versions';
import useAuthStore from '@/common/store/pinia/auth';
import useInfoStore from '@/common/store/pinia/info';
import { store } from '@/common/root';
import { DOMAIN_PROPS_KEYS } from '@/common/constants/domainProps.constants';
import takeFirst from '@/common/utils/takeFirst';
import { DEFAULT_DOMAIN_NAME } from '@/common/constants/domains.constants';
import { setPersonProperties } from '@/common/store/pinia/telemetry/core/client';
import { isAutotestEnvironment } from '@/common/store/pinia/telemetry/core/env';
import { updateUser } from '@/common/store/pinia/telemetry/modules/user';
import {
  initializeMonitors,
  cleanupMonitors,
  getPostHogBaseConfig,
  getTelemetryApiKey,
} from '@/common/store/pinia/telemetry/modules/init';
import shouldFilterError from '@/common/store/pinia/telemetry/modules/errorDenyList';
import appConfig from '@/common/appConfig';
import useThemeStore from '@/common/store/pinia/theme';

declare const APP_VERSION: string;

const useTelemetryStore = defineStore('telemetry', () => {
  const enabled = ref<boolean | null>(null);
  const eventBuffer = ref<(CaptureResult | null)[]>([]);
  const isProcessingBuffer = ref(false);
  const isInitialized = ref(false);
  const TELEMETRY_ENABLED_ENVIRONMENTS = ['production', 'playwright'];
  const themeStore = useThemeStore();

  const isTelemetryEnvironmentEnabled = computed(() => {
    if (!appConfig.app_env || !TELEMETRY_ENABLED_ENVIRONMENTS.includes(appConfig.app_env)) {
      return false;
    }

    return themeStore.themeData.useTelemetry;
  });

  const canInitializeTelemetry = ref(isTelemetryEnvironmentEnabled.value
    && Boolean(appConfig.telemetry?.apiKey)
    && Boolean(appConfig.telemetry?.host));

  const fetchAndRegisterVersionData = async (): Promise<void> => {
    try {
      const versionsStore = useVersionsStore();
      const versionData = await takeFirst(versionsStore.fetchAbout);
      const storageRelease = versionData['storage-release'];
      const storageName = versionData['storage-name'];

      setPersonProperties({
        vz_frontend_version: APP_VERSION,
        vz_product_version: storageRelease?.version,
        vz_product_release: storageRelease?.release,
        vz_storage_name: storageName
      });
    } catch (err) {
      setPersonProperties({
        vz_frontend_version: APP_VERSION,
      });
    }
  };

  const setUserData = (): void => {
    const authStore = useAuthStore();
    updateUser(authStore.currentUser, {
      isSystemAdmin: authStore.isSystemAdmin,
      isDomainAdmin: authStore.isDomainAdmin,
      isProjectAdmin: authStore.isProjectAdmin
    });
  };

  const flushEventBuffer = (): void => {
    if (isProcessingBuffer.value) {
      return;
    }

    isProcessingBuffer.value = true;
    const eventsToFlush = [...eventBuffer.value];
    eventBuffer.value = [];

    eventsToFlush.forEach(event => {
      if (event) {
        postHog.capture(event.event, event.properties, {
          timestamp: event.timestamp,
          $set: event.$set,
          $set_once: event.$set_once
        });
      }
    });

    isProcessingBuffer.value = false;
  };

  const disable = (): void => {
    enabled.value = false;
    if (!isProcessingBuffer.value) {
      eventBuffer.value = [];
    }

    cleanupMonitors();
  };

  const enable = (): void => {
    enabled.value = true;
    fetchAndRegisterVersionData();
    setUserData();

    const authStore = useAuthStore();
    if (authStore.isLoggedIn) {
      const infoStore = useInfoStore();
      infoStore.fetchAndRegisterLicenseData();
    }

    flushEventBuffer();
    initializeMonitors();
  };

  const checkTelemetryEnabled = async (): Promise<boolean> => {
    try {
      if (isAutotestEnvironment()) {
        enable();
        return true;
      }

      const result = await store.$takeFirst('domainProps/FETCH_PROP', {
        domain: DEFAULT_DOMAIN_NAME,
        key: DOMAIN_PROPS_KEYS.TELEMETRY
      });

      const isEnabled = result?.enabled ?? false;

      if (isEnabled) {
        enable();
      } else {
        disable();
      }

      return isEnabled;
    } catch (error: unknown) {
      disable();
      return false;
    }
  };

  const onTelemetryLoaded = async (): Promise<void> => {
    postHog.capture('$pageview');
    await checkTelemetryEnabled();
  };

  const resetTelemetryState = (): void => {
    cleanupMonitors();
    enabled.value = null;
    eventBuffer.value = [];
    isProcessingBuffer.value = false;
    isInitialized.value = false;
  };

  const initialize = async (): Promise<void> => {
    try {
      if (isInitialized.value || !canInitializeTelemetry.value) {
        return;
      }

      const apiKey = getTelemetryApiKey();
      const baseConfig = getPostHogBaseConfig();

      if (!apiKey || !baseConfig) {
        return;
      }

      postHog.init(apiKey, {
        ...baseConfig,
        loaded: onTelemetryLoaded,
        before_send: (event) => {
          if (!event) {
            return null;
          }

          if (shouldFilterError(event)) {
            return null;
          }

          if (enabled.value === null) {
            if (!isProcessingBuffer.value) {
              eventBuffer.value.push(event);
            }
            return null;
          }

          if (!enabled.value) {
            return null;
          }

          return event;
        }
      });

      isInitialized.value = true;
    } catch (error) {
      // Silently fail telemetry initialization
    }
  };

  const $reset = () => {
    resetTelemetryState();
  };

  return {
    enabled,
    eventBuffer,
    isProcessingBuffer,
    isInitialized,
    isTelemetryEnvironmentEnabled,
    canInitializeTelemetry,
    initialize,
    enable,
    disable,
    flushEventBuffer,
    checkTelemetryEnabled,
    resetTelemetryState,
    $reset
  };
});

export default useTelemetryStore;
